/**
 * shell/AppShell.tsx —— **全局布局壳**（装配面 · main；壳规范 v1.2）。
 *
 * 结构**只由** `shell/shell.decl.yaml` 驱动（座位序／区域／断点／视图环／可达路／设置组）：本件**不硬编码结构**，
 * 亦**不把 `views/*.yaml` 摊平当导航**（§8·R1）。界面清单**派生自** `views/*.yaml`（`viewCatalog`），
 * 界面内容由 `generateA2UI → renderDocument` 生成（**惰性 chunk**：首屏不含 antd）。
 *
 * 依据（逐字）：
 *  · §1／§1bis 三栏几何与断点｜§2 四 root 座位＋区域表｜§3 左栏＝**会话列表**｜§4 中栏（header＋视图环＋
 *    composer 上下文堆栈＋输入区）｜§5 右栏停靠（push/fullscreen；折叠零代价）｜§6 框架级浮层
 *  · §1ter 移动端视觉（4pt 栅格／字阶／组件尺寸／触控反馈／六态）｜§7 设置面板 11 组项＋S1–S3｜§8 R1–R4
 *
 * 纪律：结构与组件树**只由声明/投影生成**；**前端零取数**（禁 SQL／连接串／直连上游）；
 *      一切网络只经 `transport/`（唯一实装通道＝WS）；审批/提问＝**焦点呈现**（不常驻侧栏）；
 *      色/间距/字号**一律经 token**（禁硬编码色值）。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FOLLOW_THRESHOLD_PX, useChatFollow } from "./useChatFollow";
import type { DecisionOutcome } from "../cards/decision";
import { PreferencesProvider, usePreferences } from "../theme/PreferencesProvider";
import type { PreferencesStore } from "../theme/preferences";
import type { ApprovalCard } from "../transport/dto";
import type { FetchLike } from "../transport/writeFace";
import type { ProjectionInput } from "../views/bind";
import { loadViewFamily } from "../views/loadViews";
import { StateView } from "../views/states";
import "../styles/shell.css";
import { ABSENT_DECLARED_NOTE, ABSENT_HUMAN_LINE, ABSENT_HUMAN_NOTICE } from "./absentNote";
import { Announcer, isTerminalStatus, useTerminalAnnouncement } from "./Announcer";
import { BackStackLayer, disposeBackStack, getBackStack } from "./backstack";
import { Composer } from "./Composer";
import type { ComposerProps } from "./Composer";
import { deriveCatalog } from "../theme/modelCatalog";
import { computeColumns } from "./columns";
import { Overlay } from "./Overlay";
import { RightBar } from "./RightBar";
import { TabBodyHost, buildArtifactList, projectOrchSteps, projectProgressRows } from "../components/rightbar";
import type { ArtifactListModel } from "../components/rightbar";
import type { RightTabRecord } from "../components/RightTabStrip";
import SettingsDialog from "./SettingsDialog";
import { SettingsPanel, type ModelProviderGroup } from "./SettingsPanel";
import { SHELL_DECL, reachFor, type RightbarForm } from "./shellDecl";
import { EMPTY_DOCK, closeTab as dockCloseTab, focusTab as dockFocusTab, openAddress as dockOpenAddress, openKind as dockOpenKind, registeredKinds, seedDefault, type DockState } from "./dock";

import { Sidebar } from "./Sidebar";
import type { SessionItem, ViewEntry } from "./types";
import { useOverlayFocus } from "./useOverlayFocus";
import { useEdgeSwipe } from "./useEdgeSwipe";
import { useViewport } from "./useViewport";
import type { CommandOutboxEntry } from "./useProjection";
import { ViewRing } from "./ViewRing";

export { ABSENT_DECLARED_NOTE };

/** 视图内容的**惰性入口**（首屏不含 renderer/antd；§1bis·四「按需加载」） */
const ViewDocument = React.lazy(() => import("../views/ViewDocument"));

/**
 * 审批卡（**唯一呈现面**）的**惰性入口**：与 `ViewDocument` 同走按需 chunk，
 * 首屏（不出现等待卡时）不加载 antd 重依赖。有事件才挂载 ⇒ 无事件零代价。
 */
const ApprovalCardView = React.lazy(() =>
  import("../cards/approvalCard").then((mod) => ({ default: mod.ApprovalCardView })));

/** 界面清单：**派生自** `views/*.yaml`（禁写死；顺序＝声明装载顺序） */
export function viewCatalog(): ViewEntry[] {
  const family = loadViewFamily();
  return Object.entries(family).map(([name, decl]) => ({
    name,
    title: (decl as { title?: string }).title ?? name,
    decl,
  }));
}

export interface AppShellProps {
  /** 当前界面的投影输入（由帧状态供给；缺席即缺席） */
  inputFor: (viewName: string) => ProjectionInput;
  /** 顶栏右侧：连接状态等（由宿主注入，本壳不解释语义） */
  statusSlot?: React.ReactNode;
  /** 会话界面的上行命令回调（只有 chat 声明 input 槽）；★A-044②：返回 `false`＝发送失败（Composer 保留输入＋原因） */
  onCommand?: (text: string) => boolean | void;
  /** 发送⇄停止：运行中态 */
  running?: boolean;
  onStop?: () => void;
  /** WS 传输态（决定空态呈现 loading 还是 empty；缺省 open） */
  state?: "idle" | "connecting" | "open" | "reconnecting" | "closed";
  /** absent(declared)：声明在位、数据缺席的界面名清单（由宿主按帧状态供给） */
  absentViews?: string[];
  /** 会话列表（左栏主体；R1：导航位放对象域/任务） */
  sessions?: SessionItem[];
  /** 当前会话（座位 `main` 为 keyed；保留 key `conversation`） */
  activeSessionId?: string;
  onPickSession?: (id: string) => void;
  onCreateSession?: () => void;
  /** ★L-PL-5c：会话行删除动作（透传 Sidebar；决定动作入口形态在 Sidebar 行内，hover/焦点内揭示＋两击确认） */
  onDeleteSession?: (id: string) => void;
  /** 连接组「立即重连」（§7；实装由宿主注入） */
  onReconnect?: () => void;
  /** 高级「诊断导出」（§7；实装由宿主注入） */
  onExportDiagnostics?: () => void;
  /** §7 默认模型的**供应商分组**（★20261005 报障①订正：改由**宿主经 `transport/models` 取回**
   *  （原注「来自投影」无实现 ⇒ 面板恒空）；缺席 ⇒ 缺席呈现，零取数不补造。 */
  modelGroups?: ModelProviderGroup[];
  /** 模型录入三动作（★20261005 接线：宿主经 `transport/models` 落库；未接线 ⇒ 面板零请求＝无法保存） */
  onAddModel?: (values: { apiModel: string; displayName: string; baseUrl: string; apiKey?: string; thinkingEffort: string }) => void | Promise<void>;
  onUpdateModel?: (values: { apiModel: string; displayName: string; baseUrl: string; apiKey?: string; thinkingEffort: string; id: string }) => void | Promise<void>;
  onDeleteModel?: (id: string) => void | Promise<void>;
  /** 模型面提交中／错误（宿主注入；错误 ⇒ 面板可见提示，禁静默） */
  modelBusy?: boolean;
  modelError?: string;
  /** shell.overlay 条目（按注册序；不预置） */
  overlayEntries?: React.ReactNode[];
  /** 待办计数（审批/提问＝**焦点呈现**；R3 不常驻侧栏） */
  pendingActions?: number;
  /**
   * 等待面载荷（下行事件帧 payload；`surface==="approval"` 者成卡）——
   * **有事件即出现，无事件即无卡**（不预置、不补造）；卡在声明 `main.composer.takeover`
   * 列出的接管视图内呈现（审批/提问＝动作面焦点）。
   */
  waitCards?: ApprovalCard[];
  /** 裁决落账结果回传宿主（缺席 ⇒ 不回传；不假装成功） */
  onWaitDecided?: (outcome: DecisionOutcome) => void;
  /** 上行命令回执行（乐观「已受理中」→ `command_ack` 三态；透传 Composer） */
  outbox?: CommandOutboxEntry[];
  /** 附件上行注入面（★F2：宿主注入 `uploadApi`／`pollApi`；本壳**只透传**，不解释语义） */
  uploadDeps?: ComposerProps["uploadDeps"];
  /** 无会话时先建会话（★F2：透传 Composer 的 A 方案注入面；未注入 ⇒ 附件栏 blocked 'no-session'） */
  ensureSession?: ComposerProps["ensureSession"];
  /** 设置面事实源（缺省 ⇒ 新建，仅落 UI 偏好） */
  preferences?: PreferencesStore;
  /** U-S1 硬口径5：主体（服务端回执；宿主注入 ⇒ 面板「关于」显示行**零取数**直用。
   *  缺省 ⇒ 面板自经 `transport/session.ts::currentActor()` 懒读一次。顶栏不留身份位。 */
  actor?: string;
  /** U-S1 硬口径4/7：登出执行注入面（缺省 ⇒ 面板走 `transport/session.ts::logout()`） */
  /** 出网注入（透传给面板登出/身份缺省路径 ＋ 审批卡落账写面；测试可换） */
  fetchFn?: FetchLike;
  /**
   * 右栏三 tab 的**数据面**（宿主注入；本壳零取数、零解释）：
   *  · `sessionEntries`＝本会话下行帧载荷**原样**（progress 面就地投影：节点域认领集＋K=20 步骤聚合）
   *  · `filesModel`＝宿主经 `buildArtifactList` 装配好的列表模型（产物读面 `GET /api/files` 上线后由宿主经 transport 取回）
   *  · `textProps`＝文本预览面页数据（meta/纯文本/正文/字节；由宿主按选中文件经 transport 取回）
   *  三者**缺省即诚实缺席**（各正文自呈空/载入态；**不造假行、不预置条目**）。
   */
  sessionEntries?: unknown[];
  filesModel?: ArtifactListModel | null;
  filesState?: "loading" | "ready" | "error";
  textProps?: Record<string, unknown>;
}

/**
 * 右栏三 tab 页数据的**唯一装配处**（纯函数；与渲染同源，故可单测）。
 *
 * 纪律：本壳**零取数**——只装配宿主注入的数据；缺省即诚实缺席。
 *  · progress：`sessionEntries`（下行帧载荷原样）进同源纯函数（认领集＋K=20 聚合）；
 *  · files：宿主注入 `filesModel`（缺省＝**诚实空集** `empty:true`／`rows:[]`——不造假行）；
 *  · text：宿主注入 `textProps`（缺省＝正文自呈载入/缺席态）。
 */
export function assembleRightbarBodyProps(input: {
  sessionEntries?: unknown[];
  filesModel?: ArtifactListModel | null;
  filesState?: "loading" | "ready" | "error";
  textProps?: Record<string, unknown>;
  activeSessionId?: string;
}): Record<string, unknown> {
  const entries = input.sessionEntries ?? [];
  return {
    items: projectProgressRows(entries),
    orch: projectOrchSteps(entries),
    model: input.filesModel
      ?? buildArtifactList({ sessionId: input.activeSessionId ?? "", items: [], groups: [], total: 0 }),
    state: input.filesState ?? "ready",
    ...(input.textProps ?? {}),
  };
}

/** 数据齐否：行非空，或装配带非空事实 ⇒ 才走声明驱动渲染（否则六态空态，**不抛**） */
function hasRenderableData(input: ProjectionInput): boolean {
  if ((input.rows ?? []).length > 0) return true;
  const fact = (input.assembly?.fact ?? {}) as Record<string, unknown>;
  return Object.keys(fact).length > 0;
}

/** 主区渲染：absent ⇒ 记残（degraded）；无数据 ⇒ 六态（载入／空）；有数据 ⇒ 声明驱动（惰性渲染器） */
function renderPaneBody(
  entry: ViewEntry,
  input: ProjectionInput,
  onCommand: ((text: string) => boolean | void) | undefined,
  transportState: string,
  absent: boolean,
): React.ReactElement {
  // ★20261005 报障③：流内也**不上工程口径**（口径挂在入口 `title`；此处只给人话「记残」提示）
  if (absent) return <StateView state="degraded" notice={ABSENT_HUMAN_NOTICE} />;
  if (!hasRenderableData(input)) {
    const loading = transportState === "connecting" || transportState === "reconnecting";
    return <StateView state={loading ? "loading" : "empty"} />;
  }
  return (
    <React.Suspense fallback={<StateView state="loading" />}>
      <ViewDocument decl={entry.decl} input={input} onCommand={onCommand} />
    </React.Suspense>
  );
}

/** 界面呈现件：**每个界面一个 `view-<名>` 容器**（可达自证；徽标／视图环共用） */
function ViewPane(props: {
  entry: ViewEntry;
  input: ProjectionInput;
  onCommand?: (text: string) => boolean | void;
  transportState: string;
  absent: boolean;
  keyed?: string;
}): React.ReactElement {
  return (
    <section data-testid={"view-" + props.entry.name} data-view={props.entry.name} data-key={props.keyed}>
      {renderPaneBody(props.entry, props.input, props.onCommand, props.transportState, props.absent)}
    </section>
  );
}

export default function AppShell(props: AppShellProps): React.ReactElement {
  return (
    <PreferencesProvider store={props.preferences}>
      <AppShellInner {...props} />
    </PreferencesProvider>
  );
}

function AppShellInner(props: AppShellProps): React.ReactElement {
  const catalog = useMemo(() => viewCatalog(), []);
  const byName = useMemo(() => new Map(catalog.map((entry) => [entry.name, entry])), [catalog]);
  const absentSet = useMemo(() => new Set(props.absentViews ?? []), [props.absentViews]);
  const viewport = useViewport();
  const breakDecl = viewport.break;
  const preferences = usePreferences();
  const store = preferences.store;
  /* ★20261011 报障修（「输入框模型选择器无效 ＋ 无法发起前端测试」）：模型位与设置面板**喂同一派生返回值**
     （`theme/modelCatalog.deriveCatalog` 为唯一派生点，承 SettingsPanel `grouped_select` 同口径）——
     此前 `<Composer>` **未接** `model`／`modelCatalog` ⇒ `modelsUsable(undefined)`＝false ⇒ 发送恒禁用
     （提示「无可用模型」）＋模型位恒空。数据源＝既有 `props.modelGroups`（宿主经 `transport/models` 取回）
     ＋偏好 `default_model`，**零新数据源、零新写路径**（选中写入仍只走 `PreferencesStore.update`）。 */
  const defaultModel = preferences.snapshot.values.default_model;
  /* ★F-38（L-PF-DFT-1 采甲，20261022 大王逐字「U-S5甲」；session-权威件 v1.2 §8.4 第 5 条）：
     **全局默认模型取数位**——`model`（首级）未设 ⇒ 走全局默认兜底、**发送键可用**
     （乙案「未设模型则恒禁用」不采用）。兜底值来源＝**配置读取位**（偏好 `default_model`
      ⇒ 模型配置首项〔与后端 `pick_model_ref` 池首同义〕），**禁硬编码模型名**；
     仍为空／取数失败 ⇒ 由 `Composer` 决议位（`components/composer/modelFallback`）**显式拒＋带码可读错误**。
     ★甲案本位口径：**不再以「未设 `default_model`」为由禁用发送键**（禁用腿不回迁）。 */
  const fallbackModelSlot = useMemo(() => {
    try {
      const pref = typeof defaultModel === "string" ? defaultModel.trim() : "";
      if (pref) return { value: pref, error: null as string | null };
      const rows = props.modelGroups ?? [];
      for (const g of rows) {
        const head = g && Array.isArray(g.models) ? g.models[0] : undefined;
        const id = head && typeof head.id === "string" ? head.id.trim() : "";
        if (id) return { value: id, error: null as string | null };
      }
      return { value: "", error: null as string | null };   // 仍为空 ⇒ 决议位显式拒（E_MODEL_FALLBACK_EMPTY）
    } catch (e) {
      // 读取失败（配置缺件/异常）⇒ 显式拒（E_MODEL_FALLBACK_UNREADABLE）；禁静默放行
      return { value: "", error: e instanceof Error ? e.message : String(e) };
    }
  }, [defaultModel, props.modelGroups]);
  const composerCatalog = useMemo(() => {
    /* 目录来源＝宿主 `props.modelGroups`；**空数组视为「未装载」**（＝`null`）——
       与 `modelsUsable` 既有语义对齐（「未装载 ≠ 无可用」：未装载不阻断发送；
       装载且总数 0 才是不阻断的反面）。生产路径该数组恒由 `GET /api/models` 填充。 */
    const rows = props.modelGroups ?? [];
    const models = rows.length > 0
      ? rows.flatMap((g) => g.models.map((m) => ({ id: m.id, name: m.label, provider: g.provider })))
      : null;
    return deriveCatalog({ models, current: defaultModel });
  }, [props.modelGroups, defaultModel]);
  const status = props.state ?? "open";
  /** ★20261011：输入区上方连接条的**人面文案**（空串 ⇒ 不渲染该条；＝旧树 `v-if="connectionHint"` 语义） */
  const connHint = CONN_HINT_TEXT[status] ?? "";

  const mainSeat = SHELL_DECL.seats.find((seat) => seat.id === "main");
  const conversationKey = mainSeat?.reserved_key ?? "conversation";
  const sessionKey = props.activeSessionId ?? props.sessions?.[0]?.id ?? conversationKey;
  /* G 组（UI 批·20261011）：流容器滚动跟随控制器（全树唯一实现；外层触底阈值 25px＝FOLLOW_THRESHOLD_PX 单源，见 useChatFollow.ts） */
  const flowRef = useRef<HTMLElement | null>(null);
  const follow = useChatFollow(flowRef, { sessionKey, threshold: FOLLOW_THRESHOLD_PX });

  const home = byName.has(SHELL_DECL.viewring.home) ? SHELL_DECL.viewring.home : (catalog[0]?.name ?? "");
  const [active, setActive] = useState<string>(home);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [barOpen, setBarOpen] = useState(false);
  const [barForm, setBarForm] = useState<RightbarForm>(breakDecl.rightbar_forms[0]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /* ★20261011 报障修：设置面板**打开时定位的节**（输入框模型位 ⇒ "model"；header 齿轮 ⇒ 缺省首节）。
     设置面板按需挂载 ⇒ 每次打开都读该值。 */
  const [settingsSection, setSettingsSection] = useState<string | undefined>(undefined);
  const [takeover, setTakeover] = useState<string>("");
  const [barTab, setBarTab] = useState<string | null>(null);
  /** 右栏 tabKind ∈ 该集 ⇒ 正文走三个 tab 正文单元（其余走视图族 ViewPane） */
  /** 停靠面状态：**每会话一份**（设计件 §6.1；只在内存——刷新回折叠默认，切会话留原处） */
  const [docks, setDocks] = useState<Record<string, DockState>>({});

  // §7 会话级视图偏好（持久化）：切会话时随动（写路径仍经 store，带 revision 围栏）
  useEffect(() => {
    const saved = store.viewFor(sessionKey);
    setActive(saved && byName.has(saved) ? saved : home);
  }, [sessionKey, home, byName, store]);

  // 断点变化 ⇒ 右栏形态收敛到本档允许值（不写宽度偏好）
  useEffect(() => {
    setBarForm((current) => (breakDecl.rightbar_forms.includes(current) ? current : breakDecl.rightbar_forms[0]));
  }, [breakDecl]);

  const sidebarInFlow = breakDecl.sidebar === "resident" || breakDecl.sidebar === "collapsible" || breakDecl.sidebar === "rail";
  const sidebarOverlay = breakDecl.sidebar_overlay;
  const railCollapsed = breakDecl.sidebar === "rail";
  /** 覆盖态**展开中**（抽屉）：此时左栏走覆盖层、不占列 */
  const sidebarInDrawer = sidebarOverlay && drawerOpen;
  const sidebarInFlowNow = sidebarInFlow && !sidebarInDrawer;
  /**
   * 左栏**内容**折叠态（渲染面）：① 在流内的轨档 ⇒ 折叠（56px 轨）；
   * ② **覆盖态（抽屉）⇒ 恒展开** —— ★20261005 报障实锤：抽屉里若仍 `collapsed`，
   *    `.xg-sidebar[data-collapsed="true"] .xg-sidebar-body` 会把「＋新增会话」与会话列表
   *    整块 `visibility:hidden`（不可见、不可点）⇒ **移动端建不了会话**（本次真机复现：
   *    抽屉开（x −320→0）但 body `visibility:hidden`、`session-new` 点击即 Timeout）。
   */
  const sidebarCollapsed = sidebarInFlowNow ? (railCollapsed ? true : collapsed) : false;
  const drawerVisible = sidebarOverlay && drawerOpen;

  /** 三栏列宽：**唯一求解点**（`shell/columns.ts`）⇒ 写进壳根 inline `grid-template-columns`。
   *  右栏**默认收合**（大王 20261004 裁）；push 才占列，fullscreen 走覆盖层。 */
  const cols = useMemo(
    () => computeColumns({ break: breakDecl, sidebarCollapsed, barOpen, barForm, width: viewport.width }),
    [breakDecl, sidebarCollapsed, barOpen, barForm, viewport.width],
  );

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const drawerFocus = useOverlayFocus(drawerVisible, closeDrawer);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  const settingsFocus = useOverlayFocus(settingsOpen, closeSettings);
  const closeBar = useCallback(() => setBarOpen(false), []);
  /**
   * ★A-036（20261006）：手机档右栏＝**真全屏**（声明 `phone.rightbar_forms=[fullscreen]`，
   * 旧树口径 102「<768 右栏一律 fullscreen」）——此前落 `.xg-sheet`（`position:fixed;bottom:0;
   * max-height:90%`）⇒ 内容少时只占屏幕下部一小块。现：右栏**不再承接 `.xg-sheet`**
   * （该类职责收窄为「设置面板底部 sheet」专用），手机档落 `data-form="fullscreen"`
   * ⇒ 几何 `position:fixed; inset:0`（手机档另加 `height:100dvh`；见 `styles/shell.css`）。
   */
  const phoneFullscreen = viewport.phone;
  /** 覆盖形态（手机档真全屏 ∨ 桌面/平板 fullscreen）⇒ 视图环 tab 条被盖：两条控件改搭**面板内**（§5） */
  const barOverlay = phoneFullscreen || barForm === "fullscreen";
  /** 覆盖形态的 Escape／焦点归还（**常显入口**＝打开者）；push 展开态无浮层语义，不接管 Escape */
  const barFocus = useOverlayFocus(barOpen && barOverlay, closeBar);
  const closeTakeover = useCallback(() => setTakeover(""), []);

  /** 覆盖态**互斥**（§1bis）：左栏抽屉 ⇄ 右栏真全屏同时只开一层（打开者独占） */
  const openBar = useCallback(() => {
    setDrawerOpen(false);
    setBarOpen(true);
    // ★「首次展开才播种默认页」（§6.1.1：会话可以「折叠且空」地开始）——默认页取**声明**，非硬编码
    if (props.activeSessionId) {
      setDocks((prev) => ({ ...prev, [props.activeSessionId as string]: seedDefault(prev[props.activeSessionId as string] ?? EMPTY_DOCK) }));
    }
  }, [props.activeSessionId]);
  const openDrawer = useCallback(() => { setBarOpen(false); setDrawerOpen(true); }, []);
  /* ── 返回栈（A6①：102 backstack.js 四层语义；Esc 与返回键同一条栈）────────────
   * 协议②：每层进入 ⇒ pushState；Esc 关闭走「关闭函数」同路径（release ⇒ popTo ⇒ 浏览器历史）；
   * 返回键（popstate）由栈对账后 emit ⇒ 本处订阅逐层关 UI 状态。层级位次（102:31）：
   *   overlay(设置面) → takeover(接管态) → rightbar-fullscreen(右栏全屏) → sidebar-drawer(抽屉)。
   * 🔴 悬挂层防御（A6 实测发现）： Esc 关闭只改状态、**留一条未消费的层记录在历史里**——
   *   下一层 push 前若不收敛，该悬挂记录会让之后的返回键「白退一跳」（退到悬挂记录上，
   *   世代校验拒绝 ⇒ 事件被吞）。故入栈守卫按**历史深度**而非栈深度对账：当前记录深度
   *   与栈长不符 ⇒ 先收敛栈到历史深度，再 push（＝102 协议⑤「栈长恒等于当前历史位置的
   *   层深度」的声明式维护；`release` 走 popTo 由 popstate 逐跳驱动，不与 push 同批连打）。 */
  useEffect(() => {
    const stack = getBackStack();
    const release: Record<BackStackLayer, () => void> = {
      "overlay": closeSettings,
      "takeover": closeTakeover,
      "rightbar-fullscreen": closeBar,
      "sidebar-drawer": closeDrawer,
    };
    const open: Record<BackStackLayer, boolean> = {
      "overlay": settingsOpen,
      "takeover": takeover !== "" && byName.has(takeover),
      "rightbar-fullscreen": barOpen && phoneFullscreen,
      "sidebar-drawer": drawerVisible,
    };
    // 守卫 A（历史对账，102 协议⑤）：栈里有「状态已关」的层（Esc 遗留的悬挂层）⇒ 全部弹出。
    //   只发**一次** popTo（pendingTarget 收敛），popstate 逐跳驱动——不与 push 同批连打（★P2）。
    const stale = stack.LAYERS.filter((layer) => !open[layer] && stack.depthOf(layer) >= 0);
    if (stale.length > 0) {
      stack.popTo(stale.length === stack.depth() ? 0 : stack.depth() - stale.length);
    }
    // 守卫 B（push）：状态开且未入栈 ⇒ push（在收敛发起后追加是安全的：栈条目与历史条目
    //   一一对应，popstate 对账按 depth 走，不依赖跳数）。
    for (const layer of stack.LAYERS) {
      if (open[layer] && stack.depthOf(layer) < 0) stack.push(layer);
    }
    // 返回键驱动：popstate 对账后逐层 emit ⇒ 关对应 UI 状态（历史已消耗，状态随之收口）。
    // 🔴 pop 分支**不做 open 快照守卫**：订阅闭包里的 open 是订阅那次 render 的快照，
    //   popstate 到达时 UI 状态可能已在其后变化——而 pop 本身即「历史已消耗」的权威信号，
    //   无条件 release（关已关的状态 ＝ 幂等 no-op）；任何基于陈旧快照的拦截都会留悬挂层。
    const unsubscribe = stack.subscribe((_current, info) => {
      if (info.action === "pop") release[info.layer]?.();
    });
    return () => {
      unsubscribe();
    };
    // 依赖＝四层开态＋关闭函数（关闭函数均 useCallback 恒定）
  }, [settingsOpen, takeover, byName, barOpen, phoneFullscreen, drawerVisible, closeSettings, closeTakeover, closeBar, closeDrawer]);

  useEffect(() => () => disposeBackStack(), []);

  // §1bis·三 边缘手势（左缘右滑＝左栏；右缘左滑＝右栏真全屏）——**额外汇入路**，非唯一入口
  const swipe = useEdgeSwipe({
    onOpenSidebar: openDrawer,
    onOpenRightbar: openBar,
  });

  const { message, announce } = useTerminalAnnouncement();
  useEffect(() => {
    if (!isTerminalStatus(status)) return;      // aria-live **只播终态**（中间态不播）
    announce(status === "open" ? "连接已就绪" : "连接已断开", true);
  }, [status, announce]);

  if (catalog.length === 0) {
    return (
      <div data-testid="app-shell-empty">
        <p>无界面声明（`views/*.yaml` 缺失）</p>
      </div>
    );
  }

  const current = byName.get(active) ?? catalog[0];
  const ringEntries = catalog.filter((entry) => reachFor(entry.name).via === "viewring");
  /** 缺席入口（按目录序；正文只出**一行**人话聚合，见下） */
  const absentNames = catalog.filter((entry) => absentSet.has(entry.name));
  const badgeEntries = catalog.filter((entry) => reachFor(entry.name).via === "badge");
  const canShowBar = Boolean(sessionKey);
  /** 右栏空闲态文案（声明 `rightbar.empty`；交白面板即漂移） */
  const rightbarEmpty = SHELL_DECL.rightbar.empty ?? "未选择内容";

  /**
   * 右栏三 tab 的页数据（**零取数**：本壳只装配，网络一律归宿主/transport）。
   *  · progress：`sessionEntries` 原样进纯函数（`projectProgressRows`／`projectOrchSteps`）——
   *    无条目即空集（**不造假行**）；
   *  · files：宿主注入 `filesModel`（缺省＝诚实空集：`empty:true`、`rows:[]`）；
   *  · text：宿主注入 `textProps`（缺省＝正文自呈载入/缺席态）。
   */
  const rightbarBodyProps = useMemo<Record<string, unknown>>(
    () => assembleRightbarBodyProps(props),
    [props.sessionEntries, props.filesModel, props.filesState, props.textProps, props.activeSessionId],
  );
  /**
   * §4 接管态等待面：承载视图＝声明 `main.composer.takeover`（审批/提问）；
   * 卡源＝**下行事件帧**里 `surface==="approval"` 的载荷 ⇒ 有事件才有卡（无事件即无卡）。
   */
  const takeoverViews = SHELL_DECL.main.composer.takeover;
  const waitCards = useMemo(
    () => (props.waitCards ?? []).filter((card) => card?.surface === "approval"),
    [props.waitCards],
  );

  /** 右栏 tab 记录：★20261005 起**只来自「停靠面 tab」（tab 类型注册表：files／progress／text）**——
   *  不再拿「非环内视图 entry」冒充 tab（那是自造机制）。数据面未就绪 ⇒ 缺席即空（不造假行）。 */
  const dock = props.activeSessionId ? (docks[props.activeSessionId] ?? EMPTY_DOCK) : EMPTY_DOCK;
  const barTabs: RightTabRecord[] = dock.tabs.map((t) => ({ tabId: t.tabId, tabKind: t.kind, title: t.title, closable: t.closable }));
  const barActiveName = dock.activeTabId ?? (barTabs[0]?.tabId ?? "");
  const barActiveTab = barTabs.find((t) => t.tabId === barActiveName);
  const barActive: ViewEntry | null = byName.get(barActiveName) ?? null;

  const pickView = (name: string): void => {
    setActive(name);
    if (props.activeSessionId) store.setSessionView(props.activeSessionId, name);
  };

  const sidebarNode = (
    <Sidebar
      collapsed={sidebarCollapsed}
      onToggle={() => { if (sidebarOverlay) { if (drawerOpen) closeDrawer(); else openDrawer(); } else setCollapsed((value) => !value); }}
      sessions={props.sessions ?? []}
      activeSessionId={props.activeSessionId}
      onPickSession={(id) => { props.onPickSession?.(id); if (sidebarOverlay) setDrawerOpen(false); }}
      /* 覆盖态（抽屉）里**完成动作即收起**（与 `onPickSession` 同一条纪律：动作面不在覆盖层后留滞）——
         新建会话后抽屉仍开着会挡住输入区，用户下一次点输入区只关掉抽屉（看起来像「点了没反应」）。
         无宿主绑定 ⇒ 传 `undefined`（保住「未绑定 ⇒ 显式禁用＋可见原因」那一格）。 */
      onCreateSession={props.onCreateSession
        ? () => { props.onCreateSession?.(); if (sidebarOverlay) setDrawerOpen(false); }
        : undefined}
      /* ★L-PL-5c：删除会话动作透传（未绑定 ⇒ Sidebar 不渲染删除入口；删除**不**收抽屉——
         行仍在列表里给用户看结果，删完行退场即反馈） */
      onDeleteSession={props.onDeleteSession}
      onOpenSettings={() => { setSettingsSection(undefined); setSettingsOpen(true); }}
      settingsOpen={settingsOpen}
      actor={props.actor}
      testId={sidebarInFlowNow ? "seat-sidebar" : "sidebar-drawer"}
    />
  );

  /** 座位渲染表（**座位集合与顺序来自声明**；此处只是 id → 组件映射） */
  const seatNodes: Record<string, React.ReactNode> = {
    sidebar: sidebarInFlowNow ? (
      sidebarNode
    ) : (
      <div data-testid="seat-sidebar" data-seat="sidebar" className="xg-drawer" data-open={drawerVisible ? "true" : "false"} ref={drawerFocus.containerRef as React.RefObject<HTMLDivElement>}>
        {sidebarNode}
      </div>
    ),
    main: (
      <section data-seat="main" data-testid="seat-main" className="xg-main" key={conversationKey}>
        <header className="xg-header" data-testid="app-topbar">
          {/* 汉堡（覆盖态左栏开合；非声明面槽位——手机档独有，不参与 header 顺序判据） */}
          {!sidebarInFlow ? (
            <button
              type="button"
              className="xg-btn xg-btn-icon"
              data-testid="sidebar-hamburger"
              aria-label="会话列表"
              aria-expanded={drawerVisible}
              onClick={() => { if (drawerVisible) closeDrawer(); else openDrawer(); }}
            >☰</button>
          ) : null}
          <span className="xg-title" data-testid="app-title" data-header-slot="title">{current.title}</span>

          {/* R3：动作面＝焦点（待办徽标入口；**不常驻侧栏**）。
              ★A-033（20261006）：待办计数**唯一显示位**＝本入口徽标（`badge-count-<view>`）；
              右栏展开钮**不再挂**同一计数（原两处显示 ⇒ 「▸+0」被误读为图标）。 */}
          {badgeEntries.map((entry) => (
            <button
              key={entry.name}
              type="button"
              className="xg-btn"
              data-testid={"badge-" + entry.name}
              data-header-slot="pending_badge"
              data-reach-view={entry.name}
              data-reach-via="badge"
              aria-expanded={takeover === entry.name}
              aria-label={(reachFor(entry.name).label ?? entry.title) + pendingLabelFor(props.pendingActions ?? 0)}
              onClick={() => setTakeover((value) => (value === entry.name ? "" : entry.name))}
            >
              {reachFor(entry.name).label ?? entry.title}
              <span className="xg-badge" data-testid={"badge-count-" + entry.name} data-pending-count={props.pendingActions ?? 0}>{props.pendingActions ?? 0}</span>
            </button>
          ))}

          {/* ★20261011 报障（大王令「200 是污染文字」＋「对齐旧树」）：
              旧树同位置（`ConversationRoot.vue:31` 逐字「**任务计数徽标数字（0 = 不显示）**」）＝**任务计数**，
              而本处原渲染 `sessions.length`（**会话数**）⇒ 语义错、且 200 恒定上脸。
              修＝改**待办任务计数**（`props.pendingActions`，与待办入口同源）＋ **0 不显示**（旧树口径）。 */}
          {(props.pendingActions ?? 0) > 0 ? (
            <span className="xg-count" data-testid="task-count" data-header-slot="task_count" data-count={props.pendingActions ?? 0}>
              {props.pendingActions ?? 0}
            </span>
          ) : null}
          {/* ★20261011 报障（大王令「调整到旧树的位置，功能也要按照旧树实现」）：
              **顶栏不再承载连接态**——旧树的连接状态条在**输入区上方**（`AppShellHost.vue:1432-1449`
              `#composer` 槽内 `xg-host-composer__conn`），且**正常态不显示**（`v-if="connectionHint"`，
              文案表只有 暂停/重连中/失败/立即重连，`transport/connection.js::statusText` 为唯一来源）。
              本处原先常显「已连接」＝旧树没有的形态 ⇒ 移出顶栏（渲染迁 `Composer` 上方连接条）。
              保留的历史纪律（勿失）：**常驻界面不留身份位**——`statusSlot` 注入面退役（旧树位＝
              `ConversationRoot.vue:116-117` U-11③：用户信息→设置面板「关于」、登出→面板底部常驻动作）；
              `prop` 声明保留＝宿主旧注入不炸编译。 */}

          {/* §5 **常显入口**（★A-035：声明 `main.header` **末位**＝右上角）：
              折叠/展开两态都在位（真按钮＋`aria-expanded`＋`aria-controls`）。
              ★A-033：**不再内置待办徽标**（计数归待办入口，唯一显示位）。
              A6③ 订正（P1 批）：header 只承载**入口**（声明 `rightbar.entry: main.header`）；
              形态切换控件归声明 `rightbar.controls_slot: viewring.tail`（push）／`overlay_controls`（覆盖形态·面板内）。 */}
          {canShowBar ? (
            <button
              type="button"
              className="xg-btn xg-btn-icon"
              data-testid="rightbar-open"
              data-header-slot="rightbar_open"
              aria-label={barOpen ? "折叠右栏" : "展开右栏"}
              aria-expanded={barOpen}
              aria-controls="seat-rightbar"
              onClick={() => { if (barOpen) closeBar(); else openBar(); }}
            >
              <span aria-hidden="true">{barOpen ? "◂" : "▸"}</span>
            </button>
          ) : null}
        </header>

        <ViewRing
          entries={ringEntries}
          active={current.name}
          absent={absentSet}
          onPick={pickView}
          mode={breakDecl.viewring}
          tail={barOpen && canShowBar ? (
            <>
              {/* §5：两条控件（形态切换·折叠）搭在 tab 条末端（push 展开态）；
                  覆盖形态（桌面/平板 fullscreen ∨ 手机档真全屏）tab 条被盖 ⇒ **面板内**另给形态切换与折叠
                  （`overlay_controls`，见 RightBar），此处只留折叠口（同一 testid 不复列两枚，免歧义）。 */}
              {barOverlay ? null : (
                <button
                  type="button"
                  className="xg-btn xg-btn-icon"
                  data-testid="rightbar-form-toggle"
                  aria-label="切换右栏形态"
                  aria-pressed={false}
                  onClick={() => setBarForm("fullscreen")}
                >⤢</button>
              )}
              <button
                type="button"
                className="xg-btn xg-btn-icon"
                data-testid="rightbar-close"
                aria-label="折叠右栏"
                onClick={closeBar}
              >◂</button>
            </>
          ) : null}
        />

        {/* absent(declared) 说明：**一行聚合**（★20261005 报障③订正）。
            此前＝每个缺席入口一段**工程口径长句**（`absent(declared)：声明在位、数据缺席；入口禁用…`）
            ⇒ 页面像铺了一堵机器话（用户原话「大量的文字描述是什么鬼」）。口径本身没错，但**不该上人面正文**
            —— 现：正文一行人话＋入口名；每个入口仍各带 `title` 口径（判据 `ui_no_silent_absence` 不破）。 */}
        {absentNames.length > 0 ? (
          <p className="xg-hint" data-testid="nav-absent-line" style={{ padding: "0 var(--xg-space-md)" }}>
            {ABSENT_HUMAN_LINE}
            {absentNames.map((entry, index) => (
              <span key={entry.name}>
                <span data-testid={"nav-absent-note-" + entry.name} title={ABSENT_DECLARED_NOTE}>
                  {entry.title}
                </span>
                {index < absentNames.length - 1 ? "、" : ""}
              </span>
            ))}
          </p>
        ) : null}

        <main
          className="xg-flow"
          data-testid="view-flow"
          ref={flowRef}
          data-following={follow.followingTail ? "true" : "false"}
        >
          <ViewPane
            entry={current}
            input={props.inputFor(current.name)}
            onCommand={props.onCommand}
            transportState={status}
            absent={absentSet.has(current.name)}
            keyed={conversationKey}
          />
          {!follow.followingTail ? (
            <button
              type="button"
              className="xg-to-bottom xg-touch-44"
              data-testid="to-bottom"
              onClick={follow.jumpToBottom}
            >
              回到底部
            </button>
          ) : null}
        </main>

        {/* ★20261011（大王令「调整到旧树的位置，功能也要按照旧树实现」）：**输入区上方连接条**
            ——旧树位＝`AppShellHost.vue:1432-1449` 的 `#composer` 槽首（`xg-host-composer__conn`），
            本壳对位＝`<Composer>` 之前。**仅异常态显示**（`CONN_HINT_TEXT[status]` 非空；正常态 open ⇒ 不渲染）；
            文案单一来源＝`CONN_HINT_TEXT`（语义＝旧树 `connection.js::statusText`，不断言「网络失败」）；
            含「立即重连」按钮（旧树 `data-testid="newface-conn-retry"` 同义，本壳锚 `composer-conn-retry`）。
            live region 纪律（旧树 20260922 审计 P2）：`role="status"` ＋ `aria-live` 只落**纯文本 span**，
            按钮在播报面外（否则每次状态变化都播报按钮名）。 */}
        {connHint ? (
          <div className="xg-host-composer__conn" data-testid="composer-conn" data-conn-kind={status}>
            <span className="xg-host-composer__conn-text" role="status" aria-live="polite">
              {connHint}
            </span>
            <button
              type="button"
              data-testid="composer-conn-retry"
              className="xg-host-composer__conn-retry"
              onClick={() => props.onReconnect?.()}
            >
              立即重连
            </button>
          </div>
        ) : null}

        <Composer
          uploadDeps={props.uploadDeps}
          ensureSession={props.ensureSession}
          sessionId={sessionKey}
          model={defaultModel}
          modelCatalog={composerCatalog}
          /* ★F-38：全局默认兜底注入（取数位＝上方 `fallbackModelSlot`；判定在 Composer 决议位） */
          fallbackModel={fallbackModelSlot.value}
          fallbackReadError={fallbackModelSlot.error}
          /* ★20261011 报障修（同上一处）：模型位需**可点开**——语义＝`aria-haspopup="dialog"` ⇒ 落到**设置面板**
             （模型录入/选择区的既有载体＝`SettingsDialog`→`SettingsPanel` 的 `grouped_select`；**不另造面板**）。 */
          onOpenModel={() => { setSettingsSection("model"); setSettingsOpen(true); }}
          modelPanelOpen={settingsOpen}
          submitMode={preferences.snapshot.values.submit_mode}
          pending={props.pendingActions ?? 0}
          takeoverOpen={takeover !== "" && byName.has(takeover)}
          onOpenTakeover={() => setTakeover((value) => (value === "" ? (badgeEntries[0]?.name ?? "") : ""))}
          onCommand={props.onCommand}
          running={props.running}
          onStop={props.onStop}
          compact={viewport.phone}
          outbox={props.outbox}
          inputTestid="composer-editor"
          takeover={takeover && byName.has(takeover) ? (
            takeoverViews.includes(takeover) && waitCards.length > 0 ? (
              /* 等待面：**有事件即出现**（声明驱动的唯一呈现面＝审批卡；决策必落账）。
                 惰性 chunk ＋ Suspense：无卡时不加载、不出占位。 */
              <React.Suspense fallback={<StateView state="loading" />}>
                {waitCards.map((card, index) => (
                  <ApprovalCardView
                    key={String((card as { key?: unknown }).key ?? "wait-" + index)}
                    decl={(byName.get(takeover) as ViewEntry).decl}
                    card={card}
                    fetchFn={props.fetchFn}
                    onDecided={props.onWaitDecided}
                  />
                ))}
              </React.Suspense>
            ) : (
              /* 无等待事件 ⇒ 无卡：沿用既有视图接线自呈六态（不补造卡、不假装有待办） */
              <ViewPane
                entry={byName.get(takeover) as ViewEntry}
                input={props.inputFor(takeover)}
                onCommand={props.onCommand}
                transportState={status}
                absent={absentSet.has(takeover)}
              />
            )
          ) : undefined}
        />
      </section>
    ),
    rightbar: (
      <RightBar
        open={barOpen}
        form={barForm}
        canShow={canShowBar}
        mobileFullscreen={phoneFullscreen}
        overlay={barOverlay}
        readonly={SHELL_DECL.rightbar.readonly === true}
        emptyText={rightbarEmpty}
        onClose={closeBar}
        onToggleForm={() => setBarForm((form) => (form === "push" ? "fullscreen" : "push"))}
        trackWidthPx={cols.rightbar}
        viewportWidth={viewport.width}
        tabs={barTabs}
        activeTabId={barTab ?? barActiveName}
        onFocusTab={(tabId) => setBarTab(tabId)}
        onCloseTab={(tabId) => {
          if (!props.activeSessionId) return;
          setDocks((prev) => ({ ...prev, [props.activeSessionId as string]: dockCloseTab(prev[props.activeSessionId as string] ?? EMPTY_DOCK, tabId) }));
          setBarTab(null);
        }}
        containerRef={barOverlay ? (barFocus.containerRef as React.MutableRefObject<HTMLElement | null>) : undefined}
      >
        {/* ★20261005 收口接线（main）：右栏正文按 tabKind 分派——`files/progress/text`
            走三个 tab 正文单元（`components/rightbar` 的 TabBodyHost；A4a 建件时明写「接线归 main」，
            此前**零生产消费点**＝他点名"右侧栏吃了吗"的病根）；其余（视图族 entry）仍走 ViewPane。 */}
        {barActiveTab && TABBODY_KINDS.has(barActiveTab.tabKind) ? (
          <TabBodyHost
            /* ★F1 接线（main）：三 tab **数据面**不再空壳——progress 面就地下行帧投影；
               files/text 面由宿主注入（后端读面未上线 ⇒ 如实空态，不造假行）。 */
            bodyProps={rightbarBodyProps}
            panes={[{
              paneId: "main",
              activeTabId: barActiveName || null,
              tabs: barTabs.map((t) => ({ tabId: t.tabId, tabKind: t.tabKind, title: t.title ?? null })),
            }]}
            sessionId={props.activeSessionId ?? null}
            onOpenAddress={(e) => {
              // §6.3 地址即身份：产物行「打开」⇒ 认领类型（`sononux://file/**` ⇒ text）开**本格**新 tab
              if (!props.activeSessionId) return;
              setDocks((prev) => ({ ...prev, [props.activeSessionId as string]: dockOpenAddress(prev[props.activeSessionId as string] ?? EMPTY_DOCK, e.address) }));
            }}
            onFocusTab={(tabId) => {
              if (props.activeSessionId) {
                setDocks((prev) => ({ ...prev, [props.activeSessionId as string]: dockFocusTab(prev[props.activeSessionId as string] ?? EMPTY_DOCK, tabId) }));
              }
              setBarTab(tabId);
            }}
            onCloseTab={(tabId) => {
              if (!props.activeSessionId) return;
              setDocks((prev) => ({ ...prev, [props.activeSessionId as string]: dockCloseTab(prev[props.activeSessionId as string] ?? EMPTY_DOCK, tabId) }));
              setBarTab(null);
            }}
            onOpenKind={(e) => {
              if (!props.activeSessionId) return;
              setDocks((prev) => ({ ...prev, [props.activeSessionId as string]: dockOpenKind(prev[props.activeSessionId as string] ?? EMPTY_DOCK, e.kind) }));
            }}
          />
        ) : barActive ? (
          /* 内含视图同样**只读**（§5：右栏是详情面，不接上行命令）——**不传** `onCommand` */
          <ViewPane
            entry={barActive}
            input={props.inputFor(barActive.name)}
            transportState={status}
            absent={absentSet.has(barActive.name)}
          />
        ) : (
          /* §5 **空闲态** ⇒ 空态提示（声明 `rightbar.empty`）——不交白面板。
             ★20261006：停靠面 tab **已有生产方**（类型注册表 ＋ 每会话停靠态；展开即播种声明默认页）
             ⇒ 本态今日只剩**无会话**（无面板）与「显式选了非 tab 视图后又被清空」两路；**不造假行、不预置条目**。 */
          <div className="xg-rightbar-empty" data-testid="rightbar-empty" data-visual="xg-rightbar-empty">
            <span className="xg-state-art" aria-hidden="true" />
            <strong>{rightbarEmpty}</strong>
            <span className="xg-hint">选择内容后，详情在此呈现（只读）</span>
          </div>
        )}
      </RightBar>
    ),
    "shell.overlay": (
      <Overlay
        entries={[
          ...(props.overlayEntries ?? []),
          // U-S1：设置面板落 `shell.overlay`（AppShellHost.vue:1528 §3.0 铁律 3 逐字）；
          // 面板打开 ⇒ 追加一条层条目（toast 等宿主条目注册序不变）。
          ...(settingsOpen
            ? [(
                <SettingsDialog
                  key="settings-dialog"
                  onClose={closeSettings}
                  sidebarWidthPx={cols.sidebar}
                  rightbarWidthPx={cols.rightbar}
                  containerRef={settingsFocus.containerRef}
                >
                  <SettingsPanel
                    initialSection={settingsSection}
                    modelGroups={props.modelGroups}
                    status={status}
                    onReconnect={props.onReconnect}
                    onExportDiagnostics={props.onExportDiagnostics}
                    viewOptions={ringEntries.map((entry) => ({ name: entry.name, title: entry.title }))}
                    sessionId={props.activeSessionId}
                    actor={props.actor}
                    /* 面板取 `typeof fetch` 口径（`transport/session` 只读路径）；本件注入面为
                       `writeFace.FetchLike`（同形更窄）⇒ 此处显式收窄转换（唯一转换点，不外溢） */
                    fetchFn={props.fetchFn as unknown as typeof fetch | undefined}
                    onAddModel={props.onAddModel}
                    onUpdateModel={props.onUpdateModel}
                    onDeleteModel={props.onDeleteModel}
                    busy={props.modelBusy}
                    modelError={props.modelError}
                    onClose={closeSettings}
                  />
                </SettingsDialog>
              )]
            : []),
        ]}
      />
    ),
  };

  return (
    <div
      data-testid="app-shell"
      data-breakpoint={breakDecl.id}
      data-sidebar={breakDecl.sidebar}
      data-viewring={breakDecl.viewring}
      data-density={preferences.applied.density}
      data-theme={preferences.applied.theme}
      data-scheme={preferences.applied.resolvedScheme}
      data-col-sidebar={cols.sidebar}
      data-col-rightbar={cols.rightbar}
      style={{ gridTemplateColumns: cols.template }}
      className="xg-shell"
      onTouchStart={swipe.onTouchStart}
      onTouchEnd={swipe.onTouchEnd}
    >
      {/* 四 root 座位：集合与顺序**来自声明** */}
      {SHELL_DECL.seats.map((seat) => (
        <React.Fragment key={seat.id}>{seatNodes[seat.id] ?? null}</React.Fragment>
      ))}

      {/* 左栏抽屉遮罩（覆盖态左栏；不遮挡时零代价） */}
      {sidebarOverlay ? (
        <button
          type="button"
          className="xg-mask"
          data-testid="sidebar-mask"
          aria-label="关闭会话列表"
          hidden={!drawerVisible}
          onClick={closeDrawer}
        />
      ) : null}

      {/* 移动端右栏＝**真全屏**（§1bis／A-036）：遮罩盖住底部／抽屉（z-index 遮罩 45 ＜ 右栏 50），
          点遮罩关闭；Escape／面板内关闭同效（焦点归还打开者，见 `useOverlayFocus`） */}
      {phoneFullscreen ? (
        <button
          type="button"
          className="xg-mask"
          data-testid="rightbar-mask"
          aria-label="关闭详情面"
          hidden={!(barOpen && canShowBar)}
          onClick={closeBar}
        />
      ) : null}

      {/* 设置面板挂载段见上方 `shell.overlay` 座位条目（U-S1：§3.0 铁律 3——面板只浮在 shell.overlay，
          **不再借用右栏/sheet 类**；旧缺陷根因＝`xg-rightbar` 被网格 0 宽列压成 32px 跑出视口）。 */}

      <Announcer message={message} />
    </div>
  );
}

/** ★20261011：文案表已收口到 `shell/connText.ts`（单一来源；设置面板同源消费，禁第二份副本）。
 *  ★20261012 AUD3B FE-N-4：删转出行 `export { … } from "./connText"`（第二出口面收一；消费方一律直连 connText）。 */
import { CONN_HINT_TEXT } from "./connText";

/** 待办计数的**读屏等价**后缀（A-033：计数唯一显示位＝待办入口徽标；右栏钮不挂计数） */
function pendingLabelFor(count: number): string {
  return count > 0 ? "，待办 " + count + " 项" : "";
}

/** 连接态 → 状态点四态（**文本等价**由 `transport-status` 文本承担；点不单独承载语义） */
function statusToState(status: string): "running" | "ok" | "warn" | "error" | "idle" {
  if (status === "open") return "ok";
  if (status === "connecting" || status === "reconnecting") return "running";
  if (status === "closed") return "error";
  return "idle";
}

/** 供测试/宿主取声明（只读） */
export { SHELL_DECL } from "./shellDecl";

/** 右栏正文单元可承接的 tabKind（＝components/rightbar 注册表内的三族） */
const TABBODY_KINDS = new Set(registeredKinds());
